import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightning, ShoppingCart, ArrowRight, X, Coins } from '@phosphor-icons/react';

const CREDIT_PACKS = [
  { id: 'credits_5', credits: 5, price: 9.99, perCredit: 2.00 },
  { id: 'credits_15', credits: 15, price: 24.99, perCredit: 1.67, popular: true },
  { id: 'credits_50', credits: 50, price: 69.99, perCredit: 1.40 },
];

const WHAT_YOU_CAN_CREATE = [
  { credits: 15, items: ['1-2 video ads', '3 carousels', '5+ character images', '5+ voiceovers'] },
  { credits: 50, items: ['6 video ads', '10 carousels', '20+ character images', '20+ voiceovers'] },
];

export const CreditGateModal = ({ open, onClose, onBuyCredits, requiredCredits, availableCredits, tier }) => {
  if (!open) return null;

  const isStarter = tier === 'starter';
  const isZeroCredits = (availableCredits || 0) === 0;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="bg-zinc-900 border border-white/10 rounded-2xl max-w-lg w-full p-6 relative"
            onClick={e => e.stopPropagation()}
            data-testid="credit-gate-modal"
          >
            <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white" data-testid="credit-gate-close">
              <X size={20} />
            </button>

            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-4">
                <Coins size={28} weight="fill" className="text-amber-400" />
              </div>
              <h2 className="text-xl font-semibold text-white font-['Instrument_Serif',serif]" data-testid="credit-gate-title">
                {isZeroCredits ? "You're out of credits — keep creating" : "Not enough credits"}
              </h2>
              <p className="text-zinc-400 text-sm mt-2">
                {isStarter
                  ? "Upgrade to Pro to get 30 monthly credits, or purchase a credit pack to continue."
                  : requiredCredits
                    ? `This action needs ${requiredCredits} credit${requiredCredits > 1 ? 's' : ''}. You have ${availableCredits || 0}. Get more to continue without interruption.`
                    : "Continue generating videos, ads, and visuals instantly."
                }
              </p>
            </div>

            {/* Credit Packs */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              {CREDIT_PACKS.map(pack => (
                <button
                  key={pack.id}
                  onClick={() => onBuyCredits?.(pack.id)}
                  data-testid={`credit-gate-pack-${pack.id}`}
                  className={`relative rounded-xl p-3 text-center transition-all ${
                    pack.popular
                      ? 'bg-amber-500/10 border-2 border-amber-500/40 hover:border-amber-500/60'
                      : 'bg-zinc-800/60 border border-white/10 hover:border-white/20'
                  }`}
                >
                  {pack.popular && (
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] font-semibold bg-amber-500 text-black px-2 py-0.5 rounded-full">
                      Most Popular
                    </span>
                  )}
                  <p className="text-lg font-bold text-white mt-1">{pack.credits}</p>
                  <p className="text-[10px] text-zinc-500">credits</p>
                  <p className="text-sm font-semibold text-white mt-1">${pack.price}</p>
                  <p className="text-[10px] text-zinc-500">${pack.perCredit.toFixed(2)}/ea</p>
                </button>
              ))}
            </div>

            {/* What you can create */}
            <div className="bg-zinc-800/40 rounded-xl p-3 mb-5">
              <p className="text-xs font-medium text-zinc-300 mb-2">What you can create with 15 credits:</p>
              <div className="grid grid-cols-2 gap-1.5">
                {WHAT_YOU_CAN_CREATE[0].items.map((item, i) => (
                  <p key={i} className="text-[11px] text-zinc-400 flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-amber-400 shrink-0" />
                    {item}
                  </p>
                ))}
              </div>
            </div>

            {/* Upgrade upsell */}
            <div className="bg-zinc-800/30 border border-white/5 rounded-xl p-3 mb-4 text-center">
              <p className="text-xs text-zinc-400">
                Running out often?{' '}
                <Link to="/dashboard/billing" className="text-amber-400 hover:text-amber-300 font-medium" onClick={onClose}>
                  Upgrade your plan
                </Link>
                {' '}for more monthly credits.
              </p>
            </div>

            <button
              onClick={onClose}
              className="w-full text-center text-xs text-zinc-600 hover:text-zinc-400 transition-colors"
              data-testid="credit-gate-dismiss"
            >
              Maybe later
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CreditGateModal;
